import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';

import { FormBuilder } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { PreliminariesReportFilterModel } from 'src/app/models/preliminaries/preliminaries-report-filter.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { OrderService } from 'src/app/services/preliminaries/order.service';
import { PreliminariesReportService } from 'src/app/services/preliminaries/preliminaries-report.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';
import { SellerService } from 'src/app/services/archive/seller.service';

@Component({
  selector: 'app-pending-order',
  templateUrl: './pending-order.component.html',
  styleUrls: ['./pending-order.component.scss'],
  standalone: false
})
export class PendingOrderComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly clientService = inject(ClientService);
  private readonly costCenterService = inject(CostCenterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly orderService = inject(OrderService);
  private readonly headerService = inject(HeaderService);
  private readonly preliminariesReportService = inject(PreliminariesReportService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly sellerService = inject(SellerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;
  branchId: number = 0;
  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formPreliminariesReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listClient: any[] = [];
  listCostCenter: any[] = [];
  listOrder: any[] = [];
  listSeller: any[] = [];
  nameWindow: string = 'Informe Pedidos Pendientes';
  projectName = ProjectName.projectName;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getFirstBranchByUserAuthenticatedAsync();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  async changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.branchId = branchId;
      this.formPreliminariesReportFilter.controls["clientId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["costCenterId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["orderId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["sellerId"].patchValue(0);
    } else {
      this.formPreliminariesReportFilter.controls["branchId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["clientId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["costCenterId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["orderId"].patchValue(0);
      this.formPreliminariesReportFilter.controls["sellerId"].patchValue(0);
    }
    this.changeFilter();
    await this.getAllLists('costCenters', 'orders', 'sellers');
  }

  async changeClient(clientId: number) {
    const { controls } = this.formPreliminariesReportFilter;
    controls["costCenterId"].patchValue(0);
    controls["orderId"].patchValue(0);
    if (!clientId || clientId === 0) {
      controls["clientId"].patchValue(0);
    }
    this.changeFilter();
    await this.getAllLists('costCenters', 'orders');
  }

  async changeCostCenter(costCenterId: number | null) {
    const { controls } = this.formPreliminariesReportFilter;
    controls["orderId"].patchValue(0);
    if (!costCenterId) {
      controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
    await this.getAllLists('orders');
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeOrder(orderId: number | null) {
    const { controls } = this.formPreliminariesReportFilter;
    if (!orderId) {
      controls["orderId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeSeller(sellerId: number | null) {
    if (sellerId === 0 || sellerId === null || sellerId === undefined) {
      this.formPreliminariesReportFilter.controls["sellerId"].patchValue(0);
    }
    this.changeFilter();
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getFirstBranchByUserAuthenticatedAsync();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelPreliminariesReportFilter = this.formPreliminariesReportFilter.value as PreliminariesReportFilterModel;
      try {
        const response = await firstValueFrom(this.preliminariesReportService.exportPreliminariesReportPendingOrder(modelPreliminariesReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe Pedidos Pendientes.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'costCenters' | 'clients' | 'orders' | 'sellers'>) {
    try {
      const lists: Record<string, () => Promise<void>> = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync())
        },
        clients: async () => {
          this.listClient = await firstValueFrom(this.clientService.getAllClientsForSelectAsync())
        },
        costCenters: async () => {
          const currentClientId = this.formPreliminariesReportFilter.get('clientId')?.value || 0;
          this.listCostCenter = await firstValueFrom(this.costCenterService.getAllCostCentersForSelectByClientAsync(currentClientId, 0))
        },
        orders: async () => {
          const currentCostCenterId = this.formPreliminariesReportFilter.get('costCenterId')?.value || 0;
          this.listOrder = await firstValueFrom(this.orderService.getAllOrdersForSelect(this.branchId, currentCostCenterId))
        },
        sellers: async () => {
          this.listSeller = await firstValueFrom(this.sellerService.getAllSellersForSelectByBranchIdAsync(this.branchId));
        }
      };
      let tasks: Promise<void>[];
      if (listsToLoad.length > 0) {
        tasks = listsToLoad.map(key => lists[key]());
      } else {
        tasks = [
          lists.branches(),
          lists.clients(),
          lists.costCenters(),
          lists.orders(),
          lists.sellers()
        ];
      }
      await Promise.all(tasks);
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formPreliminariesReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.branchId = branch.branchId;
      await this.getAllLists();
    } catch (error) {
      console.error("Error al consultar la sucursal.", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formPreliminariesReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    try {
      const model = this.formPreliminariesReportFilter.value as PreliminariesReportFilterModel;
      const response = await firstValueFrom(this.preliminariesReportService.getPreliminariesReportPendingOrder(model));
      if (response && response.data?.length > 0) {
        this.dataReport = { ...response };
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast(
          'info',
          'No se encontraron datos.'
        );
      }
    } catch (error) {
      this.changeFilters = false;
      console.error("Error", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      clientId: {
        notNumber: 'Debe seleccionar un Cliente válido.'
      },
      costCenterId: {
        notNumber: 'Debe seleccionar un C.Costos válido.'
      },
      orderId: {
        notNumber: 'Debe seleccionar un Pedido válido.'
      },
      sellerId: {
        notNumber: 'Debe seleccionar un Vendedor válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formPreliminariesReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formPreliminariesReportFilter = this.formBuilder.group({
      branchId: [0],
      clientId: [0, [CustomValidators.numberOnlyValidator()]],
      costCenterId: [0, [CustomValidators.numberOnlyValidator()]],
      orderId: [0, [CustomValidators.numberOnlyValidator()]],
      sellerId: [0, [CustomValidators.numberOnlyValidator()]],
    });
  }

  validateFormControls() {
    Object.keys(this.formPreliminariesReportFilter.controls).forEach((key) => {
      const control = this.formPreliminariesReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
